import { useQuery } from "@tanstack/react-query";
const API_BASE = ""; // same-origin (SWA proxies /api/* to the linked Function App)
async function getJson(path, init) {
    const r = await fetch(API_BASE + path, init);
    if (!r.ok) {
        let body = "";
        try {
            body = await r.text();
        }
        catch { /* ignore */ }
        throw new Error(`${r.status} ${r.statusText} - ${body}`);
    }
    return (await r.json());
}
export const fetchReadyz = () => getJson("/api/readyz");
export const fetchLivez = () => getJson("/api/livez");
export const fetchRubrics = async () => (await getJson("/api/rubrics")).rubrics;
export async function submitJob(body) {
    const r = await fetch("/api/jobs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    if (r.status === 202) {
        return (await r.json());
    }
    const errBody = await r.text();
    let problem = {};
    try {
        problem = JSON.parse(errBody);
    }
    catch { /* ignore */ }
    throw new Error(problem.detail || problem.title || `${r.status} ${r.statusText}`);
}
/**
 * Fetch the caller's own public IP from api.ipify.org. Used to fill the
 * `clientIp` field on submitJob so the backend can resolve the submitter's
 * country via Azure Maps — necessary because SWA's linked-backend hop
 * strips X-Forwarded-For (see CLAUDE.md §Geo telemetry note). Treat the
 * result as best-effort: ipify can be blocked by privacy extensions /
 * corporate firewalls; in that case we just submit without a clientIp
 * and the corpus row gets a null country (which is fine).
 *
 * 4-second timeout so we never hold up a real submit waiting for an
 * IP that may never arrive. Returned IP is sent verbatim to /api/jobs
 * and is never persisted client-side.
 */
export async function fetchOwnPublicIp() {
    try {
        const ctl = new AbortController();
        const timer = setTimeout(() => ctl.abort(), 4_000);
        const r = await fetch("https://api.ipify.org?format=json", { signal: ctl.signal });
        clearTimeout(timer);
        if (!r.ok)
            return null;
        const j = await r.json();
        return typeof j.ip === "string" && j.ip.length > 0 ? j.ip : null;
    }
    catch {
        return null;
    }
}
export const getJob = (jobId) => getJson(`/api/jobs/${jobId}`);
export async function fetchAdminJobs(opts = {}) {
    const q = new URLSearchParams();
    if (opts.limit != null)
        q.set("limit", String(opts.limit));
    if (opts.month)
        q.set("month", opts.month);
    if (opts.domain)
        q.set("domain", opts.domain);
    // Path is `/api/mgmt/jobs`, not `/api/admin/jobs`. The Functions Host
    // rejects any user route starting with the literal "admin" (even
    // `admin-jobs`), and a SWA `rewrite` to translate paths would silently
    // strip x-ms-client-principal (Azure/static-web-apps#1053). The function,
    // SWA gate, and this fetch all align on the same `/api/mgmt/*` path so
    // the principal header is preserved end-to-end. See AdminJobs.cs for the
    // full constraint analysis.
    const r = await fetch(`/api/mgmt/jobs?${q}`, { credentials: "include" });
    if (r.status === 401 || r.status === 302) {
        // SWA's route gate sent us to the login flow; surface a typed signal
        // the page can render as a "sign in" prompt.
        throw new AdminAuthRequiredError();
    }
    if (!r.ok) {
        const errBody = await r.text();
        let problem = {};
        try {
            problem = JSON.parse(errBody);
        }
        catch { /* ignore */ }
        throw new Error(problem.detail || problem.title || `${r.status} ${r.statusText}`);
    }
    return (await r.json());
}
export class AdminAuthRequiredError extends Error {
    constructor() { super("Sign-in required."); this.name = "AdminAuthRequiredError"; }
}
/** Returns the approximate number of jobs sitting in the Storage Queue
 *  (submitted but not yet picked up by the evaluator). Null when the
 *  count is unavailable. Same admin-role guard as fetchAdminJobs. */
export const fetchQueueDepth = () => getJson("/api/mgmt/queue-depth");
export const fetchAuthMe = () => getJson("/.auth/me");
export function useJobPolling(jobId) {
    return useQuery({
        queryKey: ["job", jobId],
        queryFn: () => getJob(jobId),
        enabled: !!jobId,
        refetchInterval: (q) => {
            const data = q.state.data;
            if (!data)
                return 2_000;
            return data.meta.status === "Completed" || data.meta.status === "Failed" ? false : 2_000;
        },
        staleTime: 0,
    });
}
